// service/task/api/getAllTasks.ts

import { normalizeApiError } from "../../../utils/normalizeApiError";
import axiosInstance from "../../axiosInstance";
import { GetTasksSuccessResponse } from "types/project/tasks";

export interface GetAllTasksPayload {
  projectUuid: string;
  assigneeUuid?: string | null;
  reporterUuid?: string | null;
  workStatusUuidList?: string[] | null;
  workItemPriorityList?: string[] | null;
  workTypeUuidList?: string[] | null;
  createdDateFrom?: string | null;
  createdDateTo?: string | null;
  dueDateFrom?: string | null;
  dueDateTo?: string | null;
  search?: string | null; // Used for backend search by id or title
  cursor?: string | null;
  childCursor?: string | null;
  size?: number;     // default 20
  viewType?: "LIST" | "KANBAN" | null;
  hideDoneWorkItem?: boolean;
  sortBy?: string;
  sortDirection?: "ASC" | "DESC";
  skipFetch?: boolean;
}

const formatStartOfDay = (dateStr: string) => {
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '';
    return d.toISOString().split('T')[0] + 'T00:00:00Z';
  } catch {
    return '';
  }
};

const formatEndOfDay = (dateStr: string) => {
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '';
    return d.toISOString().split('T')[0] + 'T23:59:59Z';
  } catch {
    return '';
  }
};

export const getAllTasks = async (
  payload: GetAllTasksPayload
): Promise<GetTasksSuccessResponse> => {
  try {
    // Build URLSearchParams to correctly handle repeated params for array fields
    const params = new URLSearchParams();

    if (payload.projectUuid) params.append('projectUuid', payload.projectUuid);
    if (payload.viewType) params.append('viewType', payload.viewType);
    if (payload.size !== undefined) params.append('size', String(payload.size));
    if (payload.cursor) params.append('cursor', payload.cursor);
    if (payload.childCursor) params.append('childCursor', payload.childCursor);
    if (payload.sortBy) params.append('sortBy', payload.sortBy);
    if (payload.sortDirection) params.append('sortDirection', payload.sortDirection);
    if (payload.hideDoneWorkItem !== undefined) params.append('hideDoneWorkItem', String(payload.hideDoneWorkItem));
    if (payload.search) params.append('search', payload.search);
    if (payload.assigneeUuid) params.append('assigneeUuid', payload.assigneeUuid);
    if (payload.reporterUuid) params.append('reporterUuid', payload.reporterUuid);

    // ISO datetime format as required by the backend
    if (payload.createdDateFrom) {
      const formatted = formatStartOfDay(payload.createdDateFrom);
      if (formatted) params.append('createdDateFrom', formatted);
    }
    if (payload.createdDateTo) {
      const formatted = formatEndOfDay(payload.createdDateTo);
      if (formatted) params.append('createdDateTo', formatted);
    }
    if (payload.dueDateFrom) {
      const formatted = formatStartOfDay(payload.dueDateFrom);
      if (formatted) params.append('dueDateFrom', formatted);
    }
    if (payload.dueDateTo) {
      const formatted = formatEndOfDay(payload.dueDateTo);
      if (formatted) params.append('dueDateTo', formatted);
    }

    // Repeated params for list fields
    payload.workStatusUuidList?.forEach(id => params.append('workStatusUuidList', id));
    payload.workItemPriorityList?.forEach(p => params.append('workItemPriorityList', p));
    payload.workTypeUuidList?.forEach(id => params.append('workTypeUuidList', id));

    const { data } = await axiosInstance.get<GetTasksSuccessResponse>(
      `pmo/get-work-items-in-project`,
      { params }
    );

    return data;
  } catch (error) {
    throw normalizeApiError(error);
  }
};

export const exportTasks = async (payload: any) => {
  try {
    const response = await axiosInstance.post(`pmo/export-work-items`, payload, {
      responseType: "blob",
    });
    return response.data;
  } catch (error) {
    throw normalizeApiError(error);
  }
};
